/**
 * chatContextStore — Page-aware AI chat context + Chat Spaces
 *
 * Tracks:
 *   - Current page context (module, page, entity, actions available)
 *   - Chat spaces: TMS / FM / legacy FMS / WMS / Finance / General
 *   - Quick commands the AI can suggest based on current page
 *
 * Why: The AI sidebar should know WHERE the user is and WHAT they can do,
 * so it can proactively suggest actions, answer in context, and even
 * navigate/open things on behalf of the user.
 */

import { create } from 'zustand';
import { resolvedDynamicRuntimeBindingForPath, titleForResolvedRuntimePath } from '@/lib/runtime-api';

const RETAINED_CHAT_CONTEXT_KEY = 'boox_retained_chat_context_v1';

// ─── Types ─────────────────────────────────────────

export type ChatSpace =
  | 'general'
  | 'tms'
  | 'hms'
  | 'fm'
  | 'fms'
  | 'wms'
  | 'finance'
  | 'admin'
  | 'builder'
  | 'packages'
  | 'onboarding'
  | 'billing';

export interface PageContext {
  /** Current module: tms, hms, fm, legacy fms, wms, finance, admin, general */
  module: ChatSpace;
  /** Current page key like "tms.dashboard", "tms.jobs.detail" */
  pageKey: string;
  /** Human-readable page label */
  pageLabel: string;
  /** Entity ID being viewed (e.g. jobId, shipmentId) */
  entityId?: string;
  /** Entity type being viewed */
  entityType?: string;
  /** Additional metadata the AI can use */
  metadata?: Record<string, unknown>;
  taskContext?: {
    taskType?: string;
    taskGoal?: string;
    currentStep?: string;
    completionCriteria?: string[];
    missingItems?: string[];
    blockingRisks?: string[];
    recommendedNextActions?: string[];
  };
  executionContext?: {
    [key: string]: unknown;
    online?: boolean;
    scannerActive?: boolean;
    gpsAvailable?: boolean;
    photoCount?: number;
    signaturePresent?: boolean;
    selectedCustomer?: string | null;
    shiftDate?: string | null;
    batchId?: string | null;
  };
  permissionContext?: {
    [key: string]: unknown;
    canApprove?: boolean;
    canReject?: boolean;
    canDispatch?: boolean;
    canEscalate?: boolean;
    canCreateFollowUp?: boolean;
    canCaptureOnly?: boolean;
  };
  contextBudget?: {
    [key: string]: unknown;
    objectScope?: 'current_object' | 'current_surface' | 'current_thread' | string;
    threadItemsLimit?: number;
    riskLimit?: number;
    proofGapLimit?: number;
  };
}

export interface QuickAction {
  id: string;
  label: string;
  labelZh: string;
  /** The natural language prompt to send to AI */
  prompt: string;
  icon?: string;
}

/** Quick actions per page key */
const PAGE_QUICK_ACTIONS: Record<string, QuickAction[]> = {
  'tms.dashboard': [
    { id: 'today-summary', label: "Today's ops summary", labelZh: '今日运营简报', prompt: 'Give me today\'s local transport summary — active jobs, trips status, any exceptions or delays' },
    { id: 'driver-avail', label: 'Driver availability', labelZh: '查看司机可用性', prompt: 'Show me driver availability for today and tomorrow' },
    { id: 'create-job', label: 'Create transport job', labelZh: '创建运输任务', prompt: 'I want to create a new local transport job. Help me with the details.' },
  ],
  'tms.jobs': [
    { id: 'pending-jobs', label: 'Jobs needing attention', labelZh: '需要关注的任务', prompt: 'List local transport jobs that need attention — unassigned trips or approaching deadlines' },
    { id: 'create-job', label: 'Create new job', labelZh: '创建新任务', prompt: 'I want to create a new local transport job. Help me with the details.' },
  ],
  'tms.jobs.detail': [
    { id: 'job-status', label: 'Full job status', labelZh: '任务完整状态', prompt: 'Give me the full status of this job — all trips, milestones, documents, and billing status' },
    { id: 'add-trip', label: 'Add a trip', labelZh: '添加行程', prompt: 'I need to add another trip to this job.' },
    { id: 'generate-invoice', label: 'Generate invoice', labelZh: '生成发票', prompt: 'Generate invoice for this job based on the rate card and any accessorial charges' },
    { id: 'check-compliance', label: 'Compliance check', labelZh: '合规检查', prompt: 'Run compliance check on this job — GPS, POD, EIR, milestones, time windows' },
  ],
  'tms.dispatch': [
    { id: 'optimize', label: 'Optimize assignments', labelZh: '优化派车', prompt: 'Optimize today\'s dispatch — suggest best driver assignments based on proximity, capacity, and availability' },
    { id: 'replan', label: 'Replan disruption', labelZh: '异常重规划', prompt: 'A driver just reported unavailable. Help me replan the affected trips.' },
  ],
  'tms.billing': [
    { id: 'unbilled', label: 'Unbilled completed jobs', labelZh: '未开票已完成任务', prompt: 'List all completed local transport jobs that haven\'t been billed yet.' },
    { id: 'ar-aging', label: 'AR aging summary', labelZh: 'AR 账龄', prompt: 'Give me the AR aging summary for local transport — current, 30, 60, 90+ days.' },
  ],
  // ── HMS (Container Haulage) Quick Actions ──
  'hms.dashboard': [
    { id: 'today-summary', label: "Today's haulage summary", labelZh: '今日拖柜简报', prompt: 'Give me today\'s container haulage summary — active jobs, trips status, any exceptions or delays' },
    { id: 'risk-check', label: 'Check demurrage risks', labelZh: '检查滞期费风险', prompt: 'Check all containers with upcoming LFD deadlines. Any demurrage risks?' },
    { id: 'driver-avail', label: 'Driver availability', labelZh: '查看司机可用性', prompt: 'Show me driver availability for today and tomorrow' },
  ],
  'hms.jobs': [
    { id: 'pending-jobs', label: 'Jobs needing attention', labelZh: '需要关注的任务', prompt: 'List container haulage jobs that need attention — missing documents, unassigned trips, or approaching deadlines' },
    { id: 'create-job', label: 'Create new job', labelZh: '创建新任务', prompt: 'I want to create a new container haulage job. Help me with the details.' },
    { id: 'import-do', label: 'Parse a DO email', labelZh: '解析 DO 邮件', prompt: 'I have a new DO email to process. Let me paste the content.' },
  ],
  'hms.jobs.detail': [
    { id: 'job-status', label: 'Full job status', labelZh: '任务完整状态', prompt: 'Give me the full status of this job — all trips, milestones, documents, and billing status' },
    { id: 'add-trip', label: 'Add a trip', labelZh: '添加行程', prompt: 'I need to add another trip to this job.' },
    { id: 'generate-invoice', label: 'Generate invoice', labelZh: '生成发票', prompt: 'Generate invoice for this job based on the rate card and any accessorial charges' },
    { id: 'check-compliance', label: 'Compliance check', labelZh: '合规检查', prompt: 'Run compliance check on this job — GPS, POD, EIR, milestones, time windows' },
  ],
  'hms.dispatch': [
    { id: 'optimize', label: 'Optimize assignments', labelZh: '优化派车', prompt: 'Optimize today\'s dispatch — suggest best driver assignments based on proximity, capacity, and availability' },
    { id: 'street-turn', label: 'Find street turns', labelZh: '查找 Street Turn', prompt: 'Find potential street turn matches — incoming import containers that can be reused for nearby exports' },
    { id: 'replan', label: 'Replan disruption', labelZh: '异常重规划', prompt: 'A driver just reported unavailable. Help me replan the affected trips.' },
  ],
  'hms.billing': [
    { id: 'unbilled', label: 'Unbilled completed jobs', labelZh: '未开票已完成任务', prompt: 'List all completed haulage jobs that haven\'t been billed yet. Calculate total unbilled revenue.' },
    { id: 'accessorials', label: 'Check accessorial charges', labelZh: '检查附加费', prompt: 'Scan recent completed jobs for missed accessorial charges — detention, demurrage, night surcharge, weekend' },
    { id: 'ar-aging', label: 'AR aging summary', labelZh: 'AR 账龄', prompt: 'Give me the AR aging summary — current, 30, 60, 90, 90+ days. Flag any customers over credit limit.' },
  ],
  'hms.settlement': [
    { id: 'driver-pay', label: 'Driver pay summary', labelZh: '司机薪资汇总', prompt: 'Calculate this week\'s driver pay — trip fees, mileage, deductions. Show me the settlement sheet.' },
    { id: 'vendor-recon', label: 'Vendor reconciliation', labelZh: '供应商对账', prompt: 'Reconcile vendor bills against job records. Flag any discrepancies.' },
    { id: 'overdue-ar', label: 'Overdue AR actions', labelZh: '逾期 AR 处理', prompt: 'Show overdue AR and generate collection letters for 30+ day accounts' },
  ],
  'hms.pipeline': [
    { id: 'pipeline-stats', label: 'Pipeline stats', labelZh: '销售漏斗统计', prompt: 'Show me the haulage sales pipeline — how many prospects, quotes sent, won/lost this month' },
    { id: 'follow-up', label: 'Follow-ups due', labelZh: '待跟进事项', prompt: 'List all prospects and quotes that need follow-up action today' },
    { id: 'convert-rate', label: 'Win rate analysis', labelZh: '赢率分析', prompt: 'Analyze our quote-to-job conversion rate. Which customers convert best?' },
  ],
  // ── WMS (Warehouse) Quick Actions ──
  'wms.dashboard': [
    { id: 'today-summary', label: "Today's warehouse summary", labelZh: '今日仓库简报', prompt: 'Give me today\'s warehouse summary — pending ASNs, outbound orders, any exceptions' },
    { id: 'create-asn', label: 'Create inbound order', labelZh: '创建入库单', prompt: 'I want to create a new inbound order (ASN). Help me with the details.' },
    { id: 'check-inventory', label: 'Check inventory', labelZh: '查库存', prompt: 'Show me current warehouse inventory levels and any low-stock items' },
  ],
  'wms.inbound': [
    { id: 'create-asn', label: 'Create inbound order', labelZh: '创建入库单', prompt: 'I want to create a new inbound order (ASN). Help me with the details.' },
    { id: 'ai-packing-list', label: 'Import from packing list', labelZh: '用 Packing List 创建入库单', prompt: 'Help me create an inbound order from a packing list. Auto-create any missing SKUs.' },
    { id: 'pending-asn', label: 'Pending ASNs', labelZh: '待处理入库单', prompt: 'List all pending inbound orders that need receiving action' },
  ],
  'wms.inbound-orders': [
    { id: 'create-asn', label: 'Create inbound order', labelZh: '创建入库单', prompt: 'I want to create a new inbound order (ASN). Help me with the details.' },
    { id: 'ai-packing-list', label: 'Import from packing list', labelZh: '用 Packing List 创建入库单', prompt: 'Help me create an inbound order from a packing list. Auto-create any missing SKUs.' },
  ],
  'wms.outbound': [
    { id: 'pending-pick', label: 'Orders needing pick', labelZh: '待拣货订单', prompt: 'List outbound orders that need picking — show priority and deadline' },
    { id: 'create-outbound', label: 'Create outbound order', labelZh: '创建出库单', prompt: 'I want to create a new outbound order.' },
  ],
  'wms.inventory': [
    { id: 'low-stock', label: 'Low stock alerts', labelZh: '库存不足预警', prompt: 'Show any low-stock items that need reorder attention' },
    { id: 'search-sku', label: 'Search SKU', labelZh: '查找物料', prompt: 'Help me search for a specific SKU in inventory' },
  ],
  // ── Finance Quick Actions ──
  'finance.dashboard': [
    { id: 'pnl', label: 'P&L summary', labelZh: '损益汇总', prompt: 'Generate P&L summary for this month — revenue, costs, gross profit by service type' },
    { id: 'cashflow', label: 'Cash flow forecast', labelZh: '现金流预测', prompt: 'Show cash flow forecast — expected collections vs upcoming payments this week' },
    { id: 'dso', label: 'DSO trend', labelZh: 'DSO 趋势', prompt: 'What\'s our current DSO? Show the trend over the last 6 months.' },
  ],
  'builder.studio': [
    { id: 'builder-readiness', label: 'Explain publish readiness', labelZh: '解释发布就绪度', prompt: 'Explain the current builder publish readiness. Show local precheck gaps, package readiness, and the next best action.' },
    { id: 'builder-health', label: 'Summarize package health', labelZh: '汇总 package 健康度', prompt: 'Summarize the generated package health — preflight decision, simulation signals, issues, and recommended next action.' },
    { id: 'builder-share', label: 'Prepare exchange share', labelZh: '准备发布到 Exchange', prompt: 'Review whether this builder output is ready to share to Exchange. Highlight risks, missing info, and commercial implications.' },
  ],
  'builder.ai-cos-workbench': [
    { id: 'builder-create-branch', label: 'Create branch draft', labelZh: '创建分公司草稿', prompt: 'Create CMS Malaysia under CMS Group. Prepare branch draft, office setup, team roles, FM/RM/AC module profile, AI workers, data contract and owner review draft. Return any missing fields as an action form.' },
    { id: 'builder-explain-review', label: 'Explain review lock', labelZh: '解释审核锁', prompt: 'Explain why the current business component cannot go live yet. Show missing owner decisions, data contract gaps, review status and next best action.' },
    { id: 'builder-data-contract', label: 'Review data contract', labelZh: '检查数据契约', prompt: 'Review the data contract between group, branch, FM, RM, AC and AI workers. Identify required business objects and fields that need owner confirmation.' },
  ],
  'packages.manager': [
    { id: 'pack-blockers', label: 'Show blocking items', labelZh: '显示阻塞项', prompt: 'Show the current package blocking items, review items, compliance signals, and install gate reasoning.' },
    { id: 'pack-preflight', label: 'Explain preflight', labelZh: '解释预检结果', prompt: 'Explain the latest package preflight result in plain business language. What failed, why it matters, and what to fix first?' },
    { id: 'pack-simulation', label: 'Summarize simulation', labelZh: '汇总模拟结果', prompt: 'Summarize the latest simulation evidence — scenarios, approval checkpoints, risks, and operator notes.' },
    { id: 'pack-install', label: 'Why install blocked', labelZh: '为什么不能安装', prompt: 'Why is this package install blocked or review-required? Explain the exact gate, missing info, and the shortest path to ready.' },
  ],
  'onboarding.control': [
    { id: 'onboarding-health', label: 'Onboarding health', labelZh: 'Onboarding 健康度', prompt: 'Summarize the current onboarding lifecycle health — stale records, missing tasks, lane coverage, low-credit risk, and the next best actions.' },
    { id: 'onboarding-delivery', label: 'Create delivery review', labelZh: '生成交付复核建议', prompt: 'Which onboarding records should go to delivery review now, and why? Prioritize by risk, age, and missing coverage.' },
    { id: 'onboarding-sales', label: 'Commercial follow-up', labelZh: '商业跟进建议', prompt: 'Which onboarding records need commercial or sales follow-up now? Include low-credit risk, ownership concerns, and rollout ambiguity.' },
  ],
  'onboarding.records': [
    { id: 'record-gap', label: 'Explain missing info', labelZh: '解释缺失信息', prompt: 'Explain the current onboarding record gaps — required-now fields, deferred fields, escalation target, and linked tasks.' },
    { id: 'record-next', label: 'Next best action', labelZh: '下一步建议', prompt: 'For this onboarding record, what is the next best action and why? Include suggested owner, task type, and risk if delayed.' },
  ],
  'onboarding.assignment-policies': [
    { id: 'policy-review', label: 'Review assignment policy', labelZh: '审查分派策略', prompt: 'Review the current onboarding assignment policy. Is the routing sensible for sales, delivery, and support, and what should be adjusted?' },
    { id: 'policy-impact', label: 'Explain routing impact', labelZh: '解释路由影响', prompt: 'Explain how the current assignment policy affects suggested assignee selection, escalation speed, and lifecycle throughput.' },
  ],
  'billing.settings': [
    { id: 'billing-usage', label: 'Summarize usage', labelZh: '汇总用量', prompt: 'Summarize current credit usage, metered actions, recent ledger movements, and the main consumption drivers.' },
    { id: 'billing-risk', label: 'Explain low-credit risk', labelZh: '解释低额度风险', prompt: 'Explain whether the tenant is in low-credit risk, what triggered it, and what actions should happen next.' },
    { id: 'billing-upgrade', label: 'Upgrade timing', labelZh: '升级时机建议', prompt: 'Based on current usage and onboarding lifecycle, when should this tenant move from starter free mode to paid mode?' },
  ],
  'go.topic-inbox': [
    { id: 'go-topic-summary', label: 'Summarize active threads', labelZh: '汇总活跃线程', prompt: 'Summarize the current mobile topic inbox. Which object threads are most urgent, which are stale, and what should be handled first?' },
    { id: 'go-topic-reply', label: 'Draft a reply', labelZh: '起草回复', prompt: 'Based on the current object thread context, draft a concise operational reply and explain the next best action.' },
    { id: 'go-topic-escalate', label: 'Escalation advice', labelZh: '升级建议', prompt: 'Should this object thread be escalated, routed to approval, or handled directly on mobile? Explain the reasoning.' },
  ],
  'go.approvals.inbox': [
    { id: 'go-approvals-priority', label: 'Prioritize approvals', labelZh: '审批优先级建议', prompt: 'Prioritize the mobile approval inbox. Which items should be handled first and what is the operational risk if delayed?' },
    { id: 'go-approvals-brief', label: 'Approval brief', labelZh: '审批摘要', prompt: 'Summarize the pending approval inbox in plain language for a mobile reviewer. Highlight P0/P1 items and what to approve or reject first.' },
  ],
  'go.home': [
    { id: 'go-home-brief', label: 'Mobile shift brief', labelZh: '移动班次简报', prompt: 'Give me the mobile shift brief — urgent topics, approvals, exceptions, and what I should do in the next 15 minutes.' },
  ],
  'go.driver.trips': [
    { id: 'go-driver-trip-board', label: 'Trip board summary', labelZh: '行程看板摘要', prompt: 'Summarize the driver trip board. Which trips are next, which are late, and what should the driver focus on now?' },
    { id: 'go-driver-risk', label: 'Driver exception risk', labelZh: '司机异常风险', prompt: 'Check the current driver trip queue for delays, missing instructions, POD risks, or unresolved exceptions.' },
  ],
  'go.driver.trip-detail': [
    { id: 'go-driver-instruction', label: 'Summarize trip instruction', labelZh: '汇总 trip 指令', prompt: 'Summarize the current trip instruction in concise mobile ops language. Include key stops, documents, proof requirements, and next action.' },
    { id: 'go-driver-proof', label: 'Proof checklist', labelZh: '证明材料清单', prompt: 'For this trip, what photos, scans, OCR, signature, or other proof should be captured before completion?' },
    { id: 'go-driver-issue', label: 'Trip issue advice', labelZh: 'trip 异常建议', prompt: 'If this trip runs into exception or refusal, what should the driver capture and what is the escalation path?' },
  ],
  'go.wms.scan': [
    { id: 'go-wms-scan-summary', label: 'Scan loop summary', labelZh: '扫描回路摘要', prompt: 'Summarize the current warehouse scan loop. What has been captured, what is missing, and what should be corrected next?' },
    { id: 'go-wms-scan-exception', label: 'Explain scan exception', labelZh: '解释扫描异常', prompt: 'Explain the current warehouse scan exception in plain language. Which SKUs or locations are missing and what should the operator do next?' },
  ],
  'go.wms.tasks': [
    { id: 'go-wms-prioritize', label: 'Prioritize WMS tasks', labelZh: '优先级任务建议', prompt: 'Prioritize my warehouse tasks. Which one should be executed first based on urgency, blocking risk, and physical flow?' },
  ],
  'go.wms.operator': [
    { id: 'go-wms-operator-brief', label: 'Operator shift brief', labelZh: '仓务班次简报', prompt: 'Give me the warehouse operator shift brief. What inbound, pick, putaway, or release actions should I handle now?' },
  ],
  'go.scanner': [
    { id: 'go-scanner-intake', label: 'Document intake advice', labelZh: '文档入库建议', prompt: 'Based on the current scanned document, should this become order intake, receipt intake, or another AI document flow?' },
    { id: 'go-scanner-allocate', label: 'Allocate by code', labelZh: '按码绑定对象', prompt: 'Explain how to allocate this scanned document by QR/barcode or to which object/entity it likely belongs.' },
  ],
  'go.files': [
    { id: 'go-files-summary', label: 'Summarize scoped files', labelZh: '汇总当前文件', prompt: 'Summarize the currently scoped files. Which documents matter, what is missing, and which should be shared or attached next?' },
  ],
  'go.hr.attendance': [
    { id: 'go-attendance-status', label: 'Attendance status', labelZh: '考勤状态说明', prompt: 'Explain my current attendance status, GPS/clock requirements, and what I should do next if clock-in or clock-out has issues.' },
  ],
  'go.hr.leave': [
    { id: 'go-leave-guide', label: 'Leave application guide', labelZh: '请假申请指导', prompt: 'Guide me through the current leave application. What information is missing, what risks or conflicts exist, and what should I submit?' },
  ],
  'go.hr.claims': [
    { id: 'go-claims-guide', label: 'Claim submission guide', labelZh: '报销提交指导', prompt: 'Guide the current claim submission. What receipt photo or evidence is needed, how should it be classified, and what is likely to be rejected?' },
  ],
  'go.depot.field': [
    { id: 'go-depot-brief', label: 'Depot field brief', labelZh: '场站作业简报', prompt: 'Summarize the current depot field task. What checks, photos, repair or wash evidence, and signatures are required?' },
  ],
  'go.soc.field': [
    { id: 'go-soc-brief', label: 'SOC field brief', labelZh: 'SOC 作业简报', prompt: 'Summarize the current SOC field operation. What container return, grounding, or exception evidence should be captured next?' },
  ],
  'go.safety.checklists': [
    { id: 'go-safety-check', label: 'Safety sign-off reminder', labelZh: '安全签署提醒', prompt: 'Review the current safety checklist context. What sign-off, missing item, or hazard reminder should be completed before work starts?' },
  ],
  'go.safety.training': [
    { id: 'go-safety-training', label: 'Safety training summary', labelZh: '安全培训摘要', prompt: 'Summarize the current safety training requirements, missing completion items, and what the worker should watch or sign next.' },
  ],
  'go.boss.review': [
    { id: 'go-boss-review', label: 'Boss review summary', labelZh: '老板复盘摘要', prompt: 'Summarize the boss review surface. What decisions, approvals, worker/site issues, and next actions need management attention?' },
  ],
  'go.sales-boss-capture': [
    { id: 'go-sales-capture', label: 'Capture note summary', labelZh: '拜访记录摘要', prompt: 'Summarize the current sales/boss capture context. What visit note, worker list, site issue, or escalation should be recorded next?' },
  ],
};

// ─── Store ─────────────────────────────────────────

interface ChatContextState {
  /** Current page context — updated by usePageContext hook */
  pageContext: PageContext;
  setPageContext: (ctx: PageContext) => void;
  mergePageContext: (ctx: Partial<PageContext>) => void;

  /** Active chat space */
  activeSpace: ChatSpace;
  setActiveSpace: (space: ChatSpace) => void;

  /** Get quick actions for current page */
  quickActions: QuickAction[];

  /** Auto-switch space based on route */
  autoSwitchSpace: boolean;
  setAutoSwitchSpace: (v: boolean) => void;
}

const EMPTY_QUICK_ACTIONS: QuickAction[] = [];

export function getQuickActionsForPageKey(pageKey: string): QuickAction[] {
  return PAGE_QUICK_ACTIONS[pageKey] ?? EMPTY_QUICK_ACTIONS;
}

function pageContextSignature(ctx: PageContext): string {
  return JSON.stringify(ctx);
}

export const useChatContextStore = create<ChatContextState>((set, get) => ({
  pageContext: {
    module: 'general',
    pageKey: 'dashboard',
    pageLabel: 'Dashboard',
  },

  setPageContext: (ctx) => {
    const current = get().pageContext;
    if (pageContextSignature(current) === pageContextSignature(ctx)) {
      return;
    }

    set({ pageContext: ctx });
    // Auto-switch space when navigating to a different module
    if (get().autoSwitchSpace && ctx.module !== get().activeSpace) {
      set({ activeSpace: ctx.module });
    }
  },
  mergePageContext: (ctx) => {
    const next: PageContext = {
      ...get().pageContext,
      ...ctx,
      metadata: {
        ...(get().pageContext.metadata ?? {}),
        ...(ctx.metadata ?? {}),
      },
      taskContext: {
        ...(get().pageContext.taskContext ?? {}),
        ...(ctx.taskContext ?? {}),
      },
      executionContext: {
        ...(get().pageContext.executionContext ?? {}),
        ...(ctx.executionContext ?? {}),
      },
      permissionContext: {
        ...(get().pageContext.permissionContext ?? {}),
        ...(ctx.permissionContext ?? {}),
      },
      contextBudget: {
        ...(get().pageContext.contextBudget ?? {}),
        ...(ctx.contextBudget ?? {}),
      },
    };
    if (ctx.entityId === undefined) next.entityId = undefined;
    if (ctx.entityType === undefined) next.entityType = undefined;
    if (ctx.metadata === undefined) next.metadata = undefined;
    if (ctx.taskContext === undefined) next.taskContext = undefined;
    if (ctx.executionContext === undefined) next.executionContext = undefined;
    if (ctx.permissionContext === undefined) next.permissionContext = undefined;
    if (ctx.contextBudget === undefined) next.contextBudget = undefined;
    set({ pageContext: next });
  },

  activeSpace: 'general',
  setActiveSpace: (space) => set({ activeSpace: space }),

  get quickActions() {
    return getQuickActionsForPageKey(get().pageContext.pageKey);
  },

  autoSwitchSpace: true,
  setAutoSwitchSpace: (v) => set({ autoSwitchSpace: v }),
}));

export function retainNextChatContext(ctx: PageContext): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(RETAINED_CHAT_CONTEXT_KEY, JSON.stringify(ctx));
  } catch {
    // ignore storage failures
  }
}

function consumeRetainedChatContext(): PageContext | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(RETAINED_CHAT_CONTEXT_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(RETAINED_CHAT_CONTEXT_KEY);
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.module !== 'string' || typeof parsed.pageKey !== 'string' || typeof parsed.pageLabel !== 'string') {
      return null;
    }
    return parsed as PageContext;
  } catch {
    return null;
  }
}

// ─── Route → PageContext mapping ───────────────────

export function routeToPageContext(pathname: string, params?: Record<string, string>): PageContext {
  if (pathname === '/chat' || pathname.startsWith('/chat/')) {
    const retained = consumeRetainedChatContext();
    if (retained) return retained;
    return { module: 'general', pageKey: 'chat', pageLabel: 'Chat' };
  }

  if (pathname === '/' || pathname === '') {
    return { module: 'general', pageKey: 'go.home', pageLabel: 'Mobile Home' };
  }
  if (pathname === '/topic-inbox') {
    return { module: 'general', pageKey: 'go.topic-inbox', pageLabel: 'Topic Inbox' };
  }
  if (pathname === '/approvals/inbox') {
    return { module: 'general', pageKey: 'go.approvals.inbox', pageLabel: 'Approval Inbox' };
  }
  if (pathname === '/driver/trips') {
    return { module: 'tms', pageKey: 'go.driver.trips', pageLabel: 'Driver Trips' };
  }
  if (pathname.startsWith('/driver/trips/')) {
    const tripId = pathname.split('/')[3];
    return {
      module: 'tms',
      pageKey: 'go.driver.trip-detail',
      pageLabel: 'Driver Trip Detail',
      entityId: tripId,
      entityType: 'job_trip',
    };
  }
  if (pathname === '/wp/scan' || pathname === '/wms/scan') {
    return { module: 'wms', pageKey: 'go.wp.scan', pageLabel: 'WP Scan' };
  }
  if (pathname === '/wm/my-tasks' || pathname === '/wms/my-tasks') {
    return { module: 'wms', pageKey: 'go.wm.tasks', pageLabel: 'WM Tasks' };
  }
  if (pathname === '/wm/operator' || pathname === '/wms/operator') {
    return { module: 'wms', pageKey: 'go.wm.operator', pageLabel: 'WM Operator' };
  }
  if (pathname === '/scanner') {
    return { module: 'general', pageKey: 'go.scanner', pageLabel: 'Document Scanner' };
  }
  if (pathname.startsWith('/files')) {
    return { module: 'general', pageKey: 'go.files', pageLabel: 'File Share' };
  }
  if (pathname === '/depot/field') {
    return { module: 'hms', pageKey: 'go.depot.field', pageLabel: 'Depot Field' };
  }
  if (pathname === '/soc/field') {
    return { module: 'hms', pageKey: 'go.soc.field', pageLabel: 'SOC Field' };
  }
  if (pathname === '/safety/checklists') {
    return { module: 'general', pageKey: 'go.safety.checklists', pageLabel: 'Safety Checklists' };
  }
  if (pathname === '/safety/training') {
    return { module: 'general', pageKey: 'go.safety.training', pageLabel: 'Safety Training' };
  }
  if (pathname === '/boss/review') {
    return { module: 'general', pageKey: 'go.boss.review', pageLabel: 'Boss Review' };
  }
  if (pathname === '/capture/sales-boss') {
    return { module: 'general', pageKey: 'go.sales-boss-capture', pageLabel: 'Sales Boss Capture' };
  }

  // TMS routes (local transport)
  if (pathname === '/tms' || pathname === '/tms/') {
    return { module: 'tms', pageKey: 'tms.dashboard', pageLabel: 'Transport Hero V1 Dashboard' };
  }
  if (pathname.startsWith('/tms/jobs/') && params?.jobId) {
    return { module: 'tms', pageKey: 'tms.jobs.detail', pageLabel: 'Job Detail', entityId: params.jobId, entityType: 'job' };
  }
  if (pathname === '/tms/jobs') {
    return { module: 'tms', pageKey: 'tms.jobs', pageLabel: 'Jobs' };
  }
  if (pathname === '/tms/dispatch') {
    return { module: 'tms', pageKey: 'tms.dispatch', pageLabel: 'Dispatch' };
  }
  if (pathname === '/tms/billing') {
    return { module: 'tms', pageKey: 'tms.billing', pageLabel: 'Billing' };
  }
  if (pathname.startsWith('/tms/')) {
    return { module: 'tms', pageKey: 'tms.other', pageLabel: 'Transport Hero V1' };
  }

  // HMS routes (container haulage)
  if (pathname === '/hms' || pathname === '/hms/') {
    return { module: 'hms', pageKey: 'hms.dashboard', pageLabel: 'Haulage Management V1 Dashboard' };
  }
  if (pathname.startsWith('/hms/jobs/') && params?.jobId) {
    return { module: 'hms', pageKey: 'hms.jobs.detail', pageLabel: 'Job Detail', entityId: params.jobId, entityType: 'job' };
  }
  if (pathname === '/hms/jobs') {
    return { module: 'hms', pageKey: 'hms.jobs', pageLabel: 'Jobs' };
  }
  if (pathname === '/hms/dispatch') {
    return { module: 'hms', pageKey: 'hms.dispatch', pageLabel: 'Dispatch' };
  }
  if (pathname === '/hms/billing') {
    return { module: 'hms', pageKey: 'hms.billing', pageLabel: 'Billing' };
  }
  if (pathname === '/hms/settlement') {
    return { module: 'hms', pageKey: 'hms.settlement', pageLabel: 'Settlement' };
  }
  if (pathname === '/hms/pipeline') {
    return { module: 'hms', pageKey: 'hms.pipeline', pageLabel: 'Sales Pipeline' };
  }
  if (pathname.startsWith('/hms/')) {
    return { module: 'hms', pageKey: 'hms.other', pageLabel: 'HMS' };
  }

  const runtimeBinding = resolvedDynamicRuntimeBindingForPath(pathname);
  if (runtimeBinding) {
    const page = pathname.split('/')[2] ?? 'overview';
    const bindingModule = runtimeBinding.requiredModule?.toUpperCase() ?? runtimeBinding.module.toUpperCase();
    const runtimeTitle = titleForResolvedRuntimePath(pathname, runtimeBinding);
    const runtimeChatContext = runtimeBinding.chatContext;
    const isRuntimeSelfService = runtimeChatContext?.selfServicePathPrefixes?.some((prefix) => (
      pathname === prefix || pathname.startsWith(`${prefix}/`)
    )) ?? false;
    if (bindingModule === 'TM') {
      return { module: 'tms', pageKey: `tms.${page}`, pageLabel: runtimeBinding.categoryLabel ?? 'Transport' };
    }
    if (bindingModule === 'CT') {
      return { module: 'hms', pageKey: `hms.${page}`, pageLabel: runtimeBinding.categoryLabel ?? 'Haulage' };
    }
    if (bindingModule === 'WM') {
      return { module: 'wms', pageKey: `wm.${page}`, pageLabel: runtimeBinding.categoryLabel ?? 'WM' };
    }
    if (bindingModule === 'FM' || bindingModule === 'CF' || bindingModule === 'SC') {
      return { module: 'fms', pageKey: `fms.${page}`, pageLabel: runtimeBinding.categoryLabel ?? 'Freight' };
    }
    if (bindingModule === 'ACC' || bindingModule === 'AC') {
      return { module: 'finance', pageKey: `finance.${page}`, pageLabel: runtimeBinding.categoryLabel ?? 'Finance' };
    }
    if (runtimeChatContext?.module && runtimeChatContext.pageKeyPrefix) {
      const pageKeyPrefix = isRuntimeSelfService
        ? (runtimeChatContext.selfServicePageKeyPrefix ?? runtimeChatContext.pageKeyPrefix)
        : runtimeChatContext.pageKeyPrefix;
      return {
        module: runtimeChatContext.module as ChatSpace,
        pageKey: `${pageKeyPrefix}.${page}`,
        pageLabel: runtimeTitle,
      };
    }
  }

  // FM routes
  if (pathname.startsWith('/fm')) {
    const page = pathname.split('/')[2] ?? 'dashboard';
    return { module: 'fm', pageKey: `fm.${page}`, pageLabel: `FM ${page}` };
  }

  // Legacy FMS routes
  if (pathname.startsWith('/fms')) {
    const page = pathname.split('/')[2] ?? 'dashboard';
    return { module: 'fms', pageKey: `fms.${page}`, pageLabel: `FMS ${page}` };
  }

  // WM / WMS routes
  if (pathname.startsWith('/wm') || pathname.startsWith('/wms')) {
    const page = pathname.split('/')[2] ?? 'dashboard';
    return { module: 'wms', pageKey: `wm.${page}`, pageLabel: `WM ${page}` };
  }

  // Finance routes
  if (pathname.startsWith('/finance')) {
    const page = pathname.split('/')[2] ?? 'dashboard';
    return { module: 'finance', pageKey: `finance.${page}`, pageLabel: `Finance ${page}` };
  }

  // Billing settings
  if (pathname === '/settings/billing') {
    return { module: 'billing', pageKey: 'billing.settings', pageLabel: 'Billing Settings' };
  }

  // Admin routes
  if (pathname === '/admin/builder') {
    return { module: 'builder', pageKey: 'builder.studio', pageLabel: 'Builder Studio' };
  }
  if (pathname === '/builder/ai-cos-workbench') {
    return {
      module: 'builder',
      pageKey: 'builder.ai-cos-workbench',
      pageLabel: 'AI CoS Workbench',
      entityId: 'cms-group',
      entityType: 'business_group',
      metadata: {
        builderAccountMode: true,
        groupId: 'cms',
        groupLabel: 'CMS Group',
        selectedNode: 'CMS Malaysia Draft',
        allowedActions: ['preview_draft', 'action_form', 'save_review_draft', 'queue_owner_review'],
        outputCanvases: ['/builder/organization', '/builder/world-view', '/builder/review-queue'],
      },
      permissionContext: {
        canApprove: true,
        canReject: true,
        canCreateFollowUp: true,
      },
      contextBudget: {
        objectScope: 'current_surface',
        threadItemsLimit: 12,
        riskLimit: 8,
      },
    };
  }
  if (pathname.startsWith('/builder/')) {
    const page = pathname.split('/')[2] ?? 'business-home';
    return { module: 'builder', pageKey: `builder.${page}`, pageLabel: `Builder ${page}` };
  }
  if (pathname === '/admin/packs') {
    return { module: 'packages', pageKey: 'packages.manager', pageLabel: 'Business Packages' };
  }
  if (pathname === '/admin/onboarding-control') {
    return { module: 'onboarding', pageKey: 'onboarding.control', pageLabel: 'Onboarding Control' };
  }
  if (pathname === '/admin/onboarding-records') {
    return { module: 'onboarding', pageKey: 'onboarding.records', pageLabel: 'Onboarding Records' };
  }
  if (pathname === '/admin/onboarding-assignment-policies') {
    return { module: 'onboarding', pageKey: 'onboarding.assignment-policies', pageLabel: 'Onboarding Assignment Policies' };
  }
  if (pathname === '/admin/tasks') {
    return { module: 'admin', pageKey: 'admin.tasks', pageLabel: 'Task Center' };
  }
  if (pathname === '/admin/tenants') {
    return { module: 'admin', pageKey: 'admin.tenants', pageLabel: 'Tenants' };
  }
  if (pathname === '/admin/platform-dynamic') {
    return { module: 'admin', pageKey: 'admin.platform-dynamic', pageLabel: 'Platform Dynamic Home' };
  }
  if (pathname.startsWith('/admin')) {
    return { module: 'admin', pageKey: 'admin', pageLabel: 'Admin' };
  }

  return { module: 'general', pageKey: 'dashboard', pageLabel: 'Dashboard' };
}

// ─── Chat Space config ────────────────────────────

export const CHAT_SPACES = [
  { id: 'general' as const, label: 'General', labelZh: '通用', iconName: 'bot', color: 'bg-gray-100 text-gray-700' },
  { id: 'hms' as const, label: 'Haulage', labelZh: '拖柜', iconName: 'container', color: 'bg-sky-100 text-sky-700' },
  { id: 'tms' as const, label: 'Transport', labelZh: '运输', iconName: 'truck', color: 'bg-blue-100 text-blue-700' },
  { id: 'fms' as const, label: 'Freight', labelZh: '货代', iconName: 'ship', color: 'bg-purple-100 text-purple-700' },
  { id: 'wms' as const, label: 'Warehouse', labelZh: '仓储', iconName: 'warehouse', color: 'bg-green-100 text-green-700' },
  { id: 'finance' as const, label: 'Finance', labelZh: '财务', iconName: 'receipt', color: 'bg-amber-100 text-amber-700' },
  { id: 'admin' as const, label: 'Admin', labelZh: '管理', iconName: 'settings', color: 'bg-slate-100 text-slate-700' },
  { id: 'builder' as const, label: 'Builder', labelZh: '构建', iconName: 'wand', color: 'bg-violet-100 text-violet-700' },
  { id: 'packages' as const, label: 'Packages', labelZh: '业务包', iconName: 'package', color: 'bg-indigo-100 text-indigo-700' },
  { id: 'onboarding' as const, label: 'Onboarding', labelZh: '入驻', iconName: 'list-checks', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'billing' as const, label: 'Billing', labelZh: '计费', iconName: 'banknote', color: 'bg-orange-100 text-orange-700' },
] as const;
